import { useContext, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { OwnerApiError, ownerApi, sharedMe } from 'lib/owner-notes/api'
import type { CommunityHelpfulAnswer } from 'lib/community/types'
import {
  type OwnerCredential,
  useOwnerCredentialOptional,
  useOwnerSessionOptional,
} from 'lib/owner-notes/auth-bridge'
import { hasDeviceMarker, useDeviceEpoch } from 'lib/owner-notes/declared'
import {
  helpfulAnswer,
  helpfulErrorMessage,
  helpfulOwn,
  helpfulPress,
  helpfulRead,
  helpfulPendingAfterDismiss,
  helpfulReadMine,
  helpfulReplay,
  helpfulReplayDue,
  helpfulRollback,
  helpfulSeed,
  helpfulText,
  isOwnReview,
  type HelpfulPending,
  type HelpfulState,
} from 'lib/community/helpful'
import { MemberViewContext } from './useOwnMemberMe'
import styles from './Helpful.module.css'

/**
 * Where the control votes and reads when it is not on an owner review (a discussion's "Same
 * question" / Helpful, `DiscussionActions`). Absent: the owner review's routes (`ownerApi.helpful`,
 * `ownerApi.helpfulMine`) and its `own_note` refusal.
 */
export interface HelpfulTarget {
  /** Cast (`on`) or undo the vote; resolves to a `{ helpful, mine }`-shaped answer (normalised by
   *  the caller, e.g. `fromDiscussionAnswer`) — anything else rolls the press back. */
  vote: (credential: OwnerCredential, on: boolean) => Promise<unknown>
  /** The reader's own vote with no count (`helpfulReadMine`); null = unknown. */
  readMine: (credential: OwnerCredential) => Promise<boolean | null>
  /** DealHunt's error codes for "this is your own post". */
  ownCodes: ReadonlySet<string>
  /** The inert control's title on the author's own post. */
  ownTitle: string
  /** The Google-only sentence worded for this target (`discussionGoogleOnly`); absent: the owner
   *  review's `HELPFUL_GOOGLE_ONLY`. */
  googleOnly?: string
}

interface HelpfulButtonProps {
  /** The review's note id — or the post's id with a `target`. */
  noteId: string
  /** The review's author handle — the own-review check. */
  handle: string
  /** DealHunt's public `helpful_count` — what the server HTML prints. */
  count: number
  /** Ask DealHunt whether the signed-in reader marked it (`GET …/helpful/me`) — the thread page.
   *  Off on feed rows: one keyed read per row is too many; the list reads every row's vote in one
   *  batched call (`useHelpfulMine`) and passes it as `known`. */
  readMine?: boolean
  /** The reader's vote from the list's batched read (`useHelpfulMine`), once it lands — applied
   *  like the thread's read (`helpfulRead`: never over a vote's answer or one in flight). Absent:
   *  the vote stays unknown, and a press changes no count until DealHunt answers. */
  known?: CommunityHelpfulAnswer
  /** The list's memory of DealHunt's answers, by note id: a row remounted by a tab switch starts
   *  from the reader's vote instead of the cached count (`CommunityFeed`). */
  memory?: Map<string, CommunityHelpfulAnswer>
  /** `pill` (the thread's actions row): a bordered "▲ Helpful · 3". `text` (a feed card's
   *  action row, design 8a): the same words as a plain bold text button. */
  layout?: 'pill' | 'text'
  /** The word on the button: "Helpful" (default), a question's "Same question". */
  label?: string
  /** Vote and read elsewhere than an owner review (a discussion). */
  target?: HelpfulTarget
  className?: string
}

const NOTE_OWN_CODES: ReadonlySet<string> = new Set(['own_note'])

/** Signed-in only, and the bearer did not verify: the reader signs in (again). */
const SIGN_IN_CODES = new Set(['auth_required', 'auth_invalid'])

/** How long after the sign-in dialog closes a session may still be arriving (a successful
 *  sign-in closes the dialog a moment before the session reaches this render) — the photo
 *  queue's grace (`useReviewPhotos`). */
const SIGN_IN_GRACE_MS = 1500

/**
 * Helpful on an owner review (community phase 2): the count and the reader's own vote, on the
 * thread's actions row and on community feed rows.
 *
 * The server HTML (shared-cached) carries the public count only. The reader's own vote arrives
 * after hydration, only from DealHunt's keyed answers: `GET …/helpful/me` when `readMine` (the
 * thread) and the reader is signed in, the list's batched read passed as `known` (feed rows), or
 * the answer to their first press (`lib/community/helpful.ts`). Until one lands the vote is
 * unknown and a press shows no count change. `/me` (device browsers) only says whether it is their own review.
 * A press on a known vote is optimistic and rolled back on failure; signed out, it opens the sign-in prompt. On
 * the reader's own review the control is inert, titled "Your own review".
 *
 * A signed-out press is kept (one per control) and replayed as a MARK once the sign-in lands
 * (`helpfulReplay` — never a toggle, so a `/helpful/me` read landing first cannot undo it). A
 * dialog closed without a session drops it after SIGN_IN_GRACE_MS (`showLogIn()` resolves on
 * open, so the dialog's `loginOpen` falling edge is the dismissal — 0797c855).
 */
const HelpfulButton: React.VFC<HelpfulButtonProps> = ({
  noteId,
  handle,
  count,
  readMine = false,
  known,
  memory,
  layout = 'pill',
  label = 'Helpful',
  target,
  className,
}) => {
  const { showLogIn, loginOpen = false } = useOwnerSessionOptional()
  const { asVisitor } = useContext(MemberViewContext)
  const credential = useOwnerCredentialOptional()
  const epoch = useDeviceEpoch()
  const [state, setState] = useState<HelpfulState>(() => helpfulSeed(count, memory?.get(noteId)))
  const [error, setError] = useState<string | null>(null)
  // The latest state for the press handler: two quick presses must see the first one's `busy`.
  const current = useRef(state)
  current.current = state

  useEffect(() => {
    if (!hasDeviceMarker()) {
      return undefined
    }
    let live = true
    sharedMe().then(
      me => {
        if (live) {
          setState(s => helpfulOwn(s, isOwnReview(me, { noteId, handle })))
        }
      },
      () => undefined
    )
    return () => {
      live = false
    }
  }, [epoch, handle, noteId])

  // The credential object is rebuilt on every render; the token is what identifies the session.
  const token = credential?.token ?? null
  const credentialRef = useRef(credential)
  credentialRef.current = credential
  const targetRef = useRef(target)
  targetRef.current = target
  useEffect(() => {
    const cred = credentialRef.current
    if (!readMine || !token || !cred) {
      return undefined
    }
    let live = true
    const where = targetRef.current
    const read: Promise<(s: HelpfulState) => HelpfulState> = where
      ? where.readMine(cred).then(mine => s => helpfulReadMine(s, mine))
      : ownerApi.helpfulMine(cred, noteId).then(answer => s => helpfulRead(s, answer))
    read.then(
      apply => {
        if (live) {
          setState(apply)
        }
      },
      () => undefined
    )
    return () => {
      live = false
    }
  }, [noteId, readMine, token])

  useEffect(() => {
    if (known) {
      setState(s => helpfulRead(s, known))
    }
  }, [known])

  /** A press waiting for the sign-in it opened — only a NEW token replays it (`helpfulReplayDue`). */
  const pending = useRef<HelpfulPending>(null)

  const send = async (
    cred: OwnerCredential,
    before: HelpfulState,
    step: { on: boolean; next: HelpfulState }
  ): Promise<void> => {
    current.current = step.next
    setState(step.next)
    setError(null)
    try {
      const answer: unknown = target
        ? await target.vote(cred, step.on)
        : await ownerApi.helpful(cred, noteId, step.on)
      const next = helpfulAnswer(step.next, answer)
      if (!next) {
        setState(s => helpfulRollback(before, s))
        setError(helpfulErrorMessage('', null))
        return
      }
      memory?.set(noteId, { helpful: next.count, mine: next.mine })
      setState(s => helpfulAnswer(s, answer) ?? s)
    } catch (e) {
      const code = e instanceof OwnerApiError ? e.code : ''
      const own = (target?.ownCodes ?? NOTE_OWN_CODES).has(code)
      setState(s => helpfulRollback(before, s, own))
      if (SIGN_IN_CODES.has(code)) {
        // Re-signing in replays this mark, as a first press would; an undo is not replayed.
        pending.current = step.on ? { token: cred.token } : null
        void showLogIn()
      } else if (!own) {
        setError(
          helpfulErrorMessage(
            code,
            e instanceof OwnerApiError ? e.message : null,
            target?.googleOnly
          )
        )
      }
    }
  }

  const press = async (): Promise<void> => {
    const before = current.current
    if (before.own || before.busy) {
      return
    }
    if (!credential) {
      pending.current = { token: null }
      void showLogIn()
      return
    }
    pending.current = null
    const step = helpfulPress(before)
    if (step) {
      await send(credential, before, step)
    }
  }

  // The sign-in a signed-out press opened has landed: replay that press, once, as a mark.
  useEffect(() => {
    const cred = credentialRef.current
    if (!cred || !helpfulReplayDue(pending.current, token)) {
      return
    }
    pending.current = null
    const before = current.current
    const step = helpfulReplay(before)
    if (step) {
      void send(cred, before, step)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per new session token
  }, [token])

  // The dialog closed without a session: the press is dropped, never replayed by a later sign-in.
  const wasOpen = useRef(loginOpen)
  useEffect(() => {
    const closed = wasOpen.current && !loginOpen
    wasOpen.current = loginOpen
    if (!closed || !pending.current) {
      return undefined
    }
    const timer = window.setTimeout(() => {
      pending.current = helpfulPendingAfterDismiss(
        pending.current,
        credentialRef.current?.token ?? null
      )
    }, SIGN_IN_GRACE_MS)
    return () => window.clearTimeout(timer)
  }, [loginOpen])

  // The member hub's "View as visitor": the author's own control draws as a visitor sees it (the
  // press stays a no-op — `press` reads the real state). Outside a hub the context is off.
  const shown = asVisitor && state.own ? { ...state, own: false } : state

  return (
    <span className={clsx(styles.wrap, className)}>
      <button
        type="button"
        className={clsx(styles.button, {
          [styles.text]: layout === 'text',
          [styles.on]: shown.mine,
          [styles.own]: shown.own,
        })}
        aria-pressed={shown.own ? undefined : shown.mine}
        aria-disabled={shown.own || undefined}
        title={shown.own ? target?.ownTitle ?? 'Your own review' : undefined}
        onClick={() => void press()}
      >
        <span className={styles.glyph} aria-hidden="true">
          {'▲'}
        </span>
        {helpfulText(shown, label)}
      </button>
      {error && (
        <span className={styles.error} role="status">
          {error}
        </span>
      )}
    </span>
  )
}

export default HelpfulButton
